/** @module Settings: language, appearance, condition factors, scan feedback and the account. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { PageHeader } from "@/components/page-header";
import { Segmented, Switch } from "@/components/ui/controls";
import { CONDITIONS } from "@/lib/collection";
import { SUPPORTED_LANGUAGES, switchLanguage } from "@/lib/i18n";
import { signOut } from "@/lib/session";
import { applyTheme, readTheme, storeTheme, type Theme } from "@/lib/theme";
import { useSession } from "@/lib/use-session";

type Settings = Awaited<ReturnType<typeof fetchSettings>>;

async function fetchSettings() {
  const { data, response } = await api.GET("/api/v1/me/settings", {});
  if (!data) throw problemFrom(response);
  return data;
}

/** "0.85" as "85": people think of discounts in percent. */
export function asPercent(factor: string | undefined): string {
  if (factor === undefined || factor === "") return "";
  return String(Math.round(Number(factor) * 100));
}

/** "85" back to "0.85"; nonsense stays out instead of being saved. */
export function asFactor(percent: string): string | null {
  const value = Number(percent.replace(",", "."));
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  // Whole percent: 72.5 would otherwise round to 0.72 in binary fractions.
  return (Math.round(value) / 100).toFixed(2);
}

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["me", "settings"], queryFn: fetchSettings });
  const [theme, setTheme] = useState<Theme>(readTheme);
  // Typed values live here until they leave the field, so a half number is not saved.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: async (change: { factors?: Record<string, string>; ui?: Settings["ui"] }) => {
      const { data, response } = await api.PATCH("/api/v1/me/settings", {
        body: {
          condition_factors: change.factors ?? settings.data?.condition_factors ?? {},
          ui: change.ui ?? settings.data?.ui ?? {},
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["me", "settings"], data);
      setDrafts({});
      // The values of every copy depend on the factors.
      void queryClient.invalidateQueries({ queryKey: ["collection"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const factors = settings.data?.condition_factors ?? {};
  const defaults = settings.data?.default_condition_factors ?? {};
  const feedback = settings.data?.ui.scanFeedback !== false;

  return (
    <div className="flex max-w-2xl flex-col gap-[18px]">
      <PageHeader title={t("settings.title")} global={false} />

      <section className="flex flex-col gap-2">
        <h2 className="t-label">{t("language.label")}</h2>
        <Segmented
          className="self-start"
          label={t("language.label")}
          value={i18n.resolvedLanguage === "en" ? "en" : "de"}
          onChange={(language) => {
            void switchLanguage(language);
          }}
          options={SUPPORTED_LANGUAGES.map((language) => ({
            value: language,
            label: t(`language.${language}`),
          }))}
        />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="t-label">{t("settings.appearance")}</h2>
        <Segmented
          className="self-start"
          label={t("settings.appearance")}
          value={theme}
          onChange={(next) => {
            setTheme(next);
            storeTheme(next);
            applyTheme(next);
          }}
          options={[
            { value: "system", label: t("settings.theme.system"), icon: <Monitor /> },
            { value: "light", label: t("settings.theme.light"), icon: <Sun /> },
            { value: "dark", label: t("settings.theme.dark"), icon: <Moon /> },
          ]}
        />
      </section>

      <section className="flex flex-col gap-1">
        <div className="row-between">
          <h2 className="t-label">{t("settings.conditions")}</h2>
          <button
            type="button"
            className="t-sm"
            style={{ color: "var(--accent)" }}
            disabled={save.isPending || !settings.data}
            onClick={() => {
              save.mutate({ factors: defaults });
            }}
          >
            {t("settings.reset")}
          </button>
        </div>
        <p className="t-xs t-muted">{t("settings.conditionsHint")}</p>
        {CONDITIONS.map((condition) => {
          const draft = drafts[condition];
          return (
            <label
              key={condition}
              className="grid min-h-11 grid-cols-[44px_1fr_88px] items-center gap-[10px]"
            >
              <b className="num">{condition}</b>
              <span className="t-sm">{t(`settings.condition.${condition}`)}</span>
              <span className="relative">
                <input
                  inputMode="decimal"
                  className="input num h-10 pr-7 text-right"
                  aria-label={t("settings.factorOf", { condition })}
                  value={draft ?? asPercent(factors[condition])}
                  onChange={(event) => {
                    setDrafts((current) => ({ ...current, [condition]: event.target.value }));
                  }}
                  onBlur={() => {
                    if (draft === undefined) return;
                    const factor = asFactor(draft);
                    if (factor === null || factor === factors[condition]) {
                      setDrafts((current) =>
                        Object.fromEntries(
                          Object.entries(current).filter(([key]) => key !== condition),
                        ),
                      );
                      return;
                    }
                    save.mutate({ factors: { ...factors, [condition]: factor } });
                  }}
                />
                <span className="t-sm t-muted pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                  %
                </span>
              </span>
            </label>
          );
        })}
        {save.isError ? (
          <p role="alert" className="t-sm loss">
            {t("settings.saveError")}
          </p>
        ) : null}
      </section>

      <div className="row-between min-h-11">
        <span className="flex flex-col">
          <span>{t("settings.scanFeedback")}</span>
          <span className="t-xs t-muted">{t("settings.scanFeedbackHint")}</span>
        </span>
        <Switch
          checked={feedback}
          label={t("settings.scanFeedback")}
          disabled={!settings.data || save.isPending}
          onChange={(next) => {
            save.mutate({ ui: { ...(settings.data?.ui ?? {}), scanFeedback: next } });
          }}
        />
      </div>

      <section className="flex flex-col gap-2 border-t border-(--border) pt-4">
        <h2 className="t-label">{t("settings.account")}</h2>
        <span className="t-sm">
          {session.account?.display_name} ·{" "}
          <span className="t-muted">{session.account?.email}</span>
        </span>
        <button
          type="button"
          onClick={() => {
            void signOut();
          }}
          className="btn btn-secondary self-start"
        >
          <LogOut />
          {t("auth.signOut")}
        </button>
      </section>
    </div>
  );
}
